import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { tokens } from '../../theme';
import { brandApi } from '../../lib/api';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Table, Th, Td } from '../../components/Table';
import { Skeleton } from '../../components/Skeleton';
import { Modal } from '../../components/Modal';

type Tab = 'performance' | 'clips' | 'brief';

interface Campaign {
  id: string;
  title: string;
  status: string;
  budget_total: number;
  budget_spent: number;
  cpm_rate?: number | null;
  vertical?: string;
  source_video_url?: string;
  brief?: string;
  target_verified_views?: number;
  deposit_paid_at?: string | null;
  final_paid_at?: string | null;
}

interface Clip {
  id: string;
  serial_code: string;
  platform: string | null;
  submitted_url: string | null;
  status: string;
  brand_decision: 'pending' | 'approved' | 'rejected';
  brand_decision_reason: string | null;
  verified_views: number;
}

interface Data {
  campaign: Campaign;
  clips: Clip[];
  views: { captured_at: string; views: number; clip_id: string }[];
}

export function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const justPaid = params.get('paid') === '1';
  const [data, setData] = useState<Data | null>(null);
  const [tab, setTab] = useState<Tab>('performance');
  const [polling, setPolling] = useState(justPaid);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const fresh = await brandApi.campaign(id);
      setData(fresh as unknown as Data);
    } catch {
      /* swallow */
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!polling || !id) return;
    const start = Date.now();
    const t = setInterval(async () => {
      if (Date.now() - start > 30000) {
        setPolling(false);
        clearInterval(t);
        return;
      }
      try {
        const fresh = (await brandApi.campaign(id)) as unknown as Data;
        if (fresh?.campaign?.deposit_paid_at) {
          setData(fresh);
          setPolling(false);
          params.delete('paid');
          setParams(params, { replace: true });
          clearInterval(t);
        }
      } catch {
        /* keep polling */
      }
    }, 2000);
    return () => clearInterval(t);
  }, [polling, id, params, setParams]);

  if (!data) {
    return (
      <div>
        <Skeleton height={40} width="50%" />
        <div style={{ marginTop: 24 }}>
          <Skeleton height={400} />
        </div>
      </div>
    );
  }

  const c = data.campaign;
  const fundedShown = !!c.deposit_paid_at;

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <span
              style={{
                fontSize: 11,
                letterSpacing: '0.35em',
                textTransform: 'uppercase',
                color: tokens.color.ink[500],
              }}
            >
              Campaign
            </span>
            <h1
              style={{
                fontFamily: tokens.font.body,
                fontSize: 40,
                fontWeight: 400,
                fontStyle: 'normal',
                marginTop: 8,
              }}
            >
              {c.title}
            </h1>
          </div>
          <Badge status={c.status as Parameters<typeof Badge>[0]['status']} />
        </div>
        {polling && !fundedShown && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: tokens.color.paper['00'],
              border: `1px solid ${tokens.color.line[100]}`,
              borderRadius: 8,
              fontSize: 13,
              color: tokens.color.ink[500],
            }}
          >
            Payment processing, this can take a moment.
          </div>
        )}
      </header>

      <nav
        style={{
          display: 'flex',
          gap: 32,
          borderBottom: `1px solid ${tokens.color.line[100]}`,
          marginBottom: 32,
        }}
      >
        {(['performance', 'clips', 'brief'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '12px 0',
              borderBottom: tab === t ? `2px solid ${tokens.color.ink[900]}` : '2px solid transparent',
              fontSize: 14,
              color: tab === t ? tokens.color.ink[900] : tokens.color.ink[500],
              fontWeight: 500,
              textTransform: 'capitalize',
            }}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === 'performance' && <Performance campaign={c} clips={data.clips} views={data.views} />}
      {tab === 'clips' && <ClipsTab campaignId={c.id} clips={data.clips} onChange={load} />}
      {tab === 'brief' && <BriefTab campaign={c} />}
    </div>
  );
}

function Performance({ campaign, clips, views }: { campaign: Campaign; clips: Clip[]; views: Array<{ captured_at: string; views: number; clip_id: string }> }) {
  const verified = clips.filter((c) => c.brand_decision === 'approved');
  const totalViews = verified.reduce((s, c) => s + (c.verified_views ?? 0), 0);
  const spentCents = campaign.budget_spent ?? 0;
  const totalCents = campaign.budget_total ?? 1;
  const remainingCents = Math.max(0, totalCents - spentCents);
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 24,
          marginBottom: 32,
        }}
      >
        <Stat label="Verified views" value={totalViews.toLocaleString()} />
        <Stat label="Active clips" value={String(verified.length)} />
        <Stat label="Spent" value={`$${(spentCents / 100).toLocaleString()}`} />
        <Stat label="Remaining" value={`$${(remainingCents / 100).toLocaleString()}`} />
      </div>

      <ViewsChart views={views} />

      <Table>
        <thead>
          <tr>
            <Th>Serial</Th>
            <Th>Platform</Th>
            <Th align="right">Verified views</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          {verified.map((c) => (
            <tr key={c.id}>
              <Td>{c.serial_code}</Td>
              <Td>{c.platform ?? '-'}</Td>
              <Td align="right">{c.verified_views?.toLocaleString() ?? 0}</Td>
              <Td>
                <Badge status="active">Approved</Badge>
              </Td>
            </tr>
          ))}
          {verified.length === 0 && (
            <tr>
              <Td>
                <span style={{ color: tokens.color.ink[500] }}>No verified clips yet.</span>
              </Td>
              <Td>{' '}</Td>
              <Td>{' '}</Td>
              <Td>{' '}</Td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

function ClipsTab({
  campaignId,
  clips,
  onChange,
}: {
  campaignId: string;
  clips: Clip[];
  onChange: () => void;
}) {
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  async function approve(clipId: string) {
    await brandApi.decideClip(campaignId, clipId, 'approve');
    onChange();
  }
  async function confirmReject() {
    if (!rejecting) return;
    await brandApi.decideClip(campaignId, rejecting, 'reject', reason);
    setRejecting(null);
    setReason('');
    onChange();
  }

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 24,
        }}
      >
        {clips.map((cl) => (
          <div
            key={cl.id}
            style={{
              background: tokens.color.paper['00'],
              border: `1px solid ${tokens.color.line[100]}`,
              borderRadius: 12,
              padding: 20,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <span style={{ fontFamily: tokens.font.mono, fontSize: 11, color: tokens.color.ink[500] }}>
                {cl.serial_code}
              </span>
              <Badge status={cl.brand_decision} />
            </div>
            {cl.submitted_url ? (
              <a
                href={cl.submitted_url}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 13, color: tokens.color.ink[500], wordBreak: 'break-all' }}
              >
                {cl.submitted_url}
              </a>
            ) : (
              <span style={{ fontSize: 13, color: tokens.color.ink[500] }}>No URL yet</span>
            )}
            <div
              style={{
                marginTop: 12,
                fontFamily: tokens.font.mono,
                fontSize: 12,
                color: tokens.color.ink[700],
              }}
            >
              {cl.platform ?? 'pending'} · {(cl.verified_views ?? 0).toLocaleString()} views
            </div>
            {cl.brand_decision === 'pending' && (
              <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                <Button size="sm" onClick={() => approve(cl.id)}>
                  Approve
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setRejecting(cl.id)}>
                  Reject
                </Button>
              </div>
            )}
            {cl.brand_decision === 'rejected' && cl.brand_decision_reason && (
              <p style={{ marginTop: 12, fontSize: 12, color: tokens.color.accent.rust }}>
                Reason: {cl.brand_decision_reason}
              </p>
            )}
          </div>
        ))}
        {clips.length === 0 && (
          <div
            style={{
              gridColumn: '1 / -1',
              padding: 64,
              textAlign: 'center',
              border: `1px dashed ${tokens.color.line[100]}`,
              borderRadius: 12,
              color: tokens.color.ink[500],
            }}
          >
            <span style={{ fontFamily: tokens.font.body, fontStyle: 'normal', fontSize: 24 }}>
              No clips submitted yet.
            </span>
            <p style={{ marginTop: 8, fontSize: 13 }}>They'll appear here as creators post.</p>
          </div>
        )}
      </div>
      <Modal open={!!rejecting} onClose={() => setRejecting(null)} title="Reject clip">
        <p style={{ fontSize: 14, color: tokens.color.ink[500] }}>
          Tell the creator why so we can route a better clip next time.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={5}
          style={{
            marginTop: 16,
            padding: 12,
            width: '100%',
            border: `1px solid ${tokens.color.line[100]}`,
            borderRadius: 8,
            fontFamily: tokens.font.body,
            fontSize: 14,
          }}
        />
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button variant="ghost" onClick={() => setRejecting(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmReject}>
            Reject clip
          </Button>
        </div>
      </Modal>
    </>
  );
}

function BriefTab({ campaign }: { campaign: Campaign }) {
  return (
    <div style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Row label="Source video">
        {campaign.source_video_url ? (
          <a
            href={campaign.source_video_url}
            style={{ fontFamily: tokens.font.mono, fontSize: 13 }}
            target="_blank"
            rel="noreferrer"
          >
            {campaign.source_video_url}
          </a>
        ) : (
          '-'
        )}
      </Row>
      <Row label="Vertical">{campaign.vertical}</Row>
      <Row label="Budget total">${(campaign.budget_total / 100).toLocaleString()}</Row>
      <Row label="CPM rate">${campaign.cpm_rate?.toFixed(2)} per 1K</Row>
      <Row label="Target views">{campaign.target_verified_views?.toLocaleString() ?? '-'}</Row>
      <Row label="Deposit paid">
        {campaign.deposit_paid_at ? new Date(campaign.deposit_paid_at).toLocaleString() : 'Not yet'}
      </Row>
      <Row label="Final paid">
        {campaign.final_paid_at
          ? new Date(campaign.final_paid_at).toLocaleString()
          : 'Pending completion'}
      </Row>
      <Row label="Brief">
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{campaign.brief}</div>
      </Row>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 16 }}>
      <span
        style={{
          fontSize: 11,
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          color: tokens.color.ink[500],
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 14, color: tokens.color.ink[900] }}>{children}</span>
    </div>
  );
}

/**
 * SVG sparkline of verified views accumulating over time. Aggregates the
 * per-clip clip_views snapshots into a single campaign-level cumulative
 * line. Shows an empty state when nothing's verified yet.
 */
function ViewsChart({
  views,
}: {
  views: Array<{ captured_at: string; views: number; clip_id: string }>;
}) {
  if (!views || views.length === 0) {
    return (
      <div
        style={{
          padding: 56,
          marginBottom: 32,
          background: tokens.color.paper['00'],
          border: `1px dashed ${tokens.color.line[100]}`,
          borderRadius: 12,
          textAlign: 'center',
          color: tokens.color.ink[500],
        }}
      >
        <span style={{ fontFamily: tokens.font.body, fontStyle: 'normal', fontSize: 22 }}>
          Verified views appear here.
        </span>
        <p style={{ marginTop: 8, fontSize: 13 }}>
          We refresh hourly once approved clips start accumulating views.
        </p>
      </div>
    );
  }

  // Group by hour timestamp and sum to a cumulative campaign-level series.
  const buckets = new Map<string, number>();
  for (const v of views) {
    const bucket = (v.captured_at ?? '').slice(0, 13); // YYYY-MM-DDTHH
    buckets.set(bucket, (buckets.get(bucket) ?? 0) + (v.views ?? 0));
  }
  const series = [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .reduce<{ at: string; cum: number }[]>((acc, [at, n], i) => {
      const prev = i > 0 ? acc[i - 1].cum : 0;
      acc.push({ at, cum: prev + n });
      return acc;
    }, []);

  if (series.length === 0) return null;

  const w = 800;
  const h = 220;
  const pad = { l: 56, r: 24, t: 24, b: 36 };
  const max = Math.max(1, ...series.map((p) => p.cum));
  const xStep = (w - pad.l - pad.r) / Math.max(1, series.length - 1);
  const path = series
    .map((p, i) => {
      const x = pad.l + i * xStep;
      const y = pad.t + (h - pad.t - pad.b) * (1 - p.cum / max);
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  const area =
    `${path} L ${pad.l + (series.length - 1) * xStep} ${h - pad.b} L ${pad.l} ${h - pad.b} Z`;

  const xTicks = [series[0], series[series.length - 1]];

  return (
    <div
      style={{
        marginBottom: 32,
        padding: 24,
        background: tokens.color.paper['00'],
        border: `1px solid ${tokens.color.line[100]}`,
        borderRadius: 12,
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          color: tokens.color.ink[500],
          marginBottom: 12,
        }}
      >
        Verified views — cumulative
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="220" style={{ display: 'block' }}>
        <defs>
          <linearGradient id="vg" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={tokens.color.ink[500]} stopOpacity="0.18" />
            <stop offset="100%" stopColor={tokens.color.ink[500]} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* y-axis grid */}
        {[0.25, 0.5, 0.75, 1].map((f) => {
          const y = pad.t + (h - pad.t - pad.b) * (1 - f);
          return (
            <g key={f}>
              <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke={tokens.color.line[100]} strokeDasharray="2 4" />
              <text
                x={pad.l - 10}
                y={y + 4}
                textAnchor="end"
                fontSize="11"
                fill={tokens.color.ink[500]}
                fontFamily={tokens.font.mono}
              >
                {Math.round(max * f).toLocaleString()}
              </text>
            </g>
          );
        })}
        <path d={area} fill="url(#vg)" />
        <path d={path} fill="none" stroke={tokens.color.ink[500]} strokeWidth="2" />
        {/* x-axis ends */}
        {xTicks.map((p, i) => (
          <text
            key={i}
            x={i === 0 ? pad.l : w - pad.r}
            y={h - 12}
            textAnchor={i === 0 ? 'start' : 'end'}
            fontSize="11"
            fill={tokens.color.ink[500]}
            fontFamily={tokens.font.mono}
          >
            {p.at.slice(0, 10)}
          </text>
        ))}
      </svg>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: 24,
        background: tokens.color.paper['00'],
        border: `1px solid ${tokens.color.line[100]}`,
        borderRadius: 12,
      }}
    >
      <span
        style={{
          fontSize: 11,
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          color: tokens.color.ink[500],
        }}
      >
        {label}
      </span>
      <div
        style={{ fontFamily: tokens.font.body, fontSize: 32, fontWeight: 400, marginTop: 8 }}
      >
        {value}
      </div>
    </div>
  );
}
